import { collection, addDoc, updateDoc, doc, getDocs, Timestamp } from '@/utils/firebase/lazyFirestore'
import type { TopMidpointEvaluation, MentoringCheckpoint } from '#shared/types/MentoringMeeting'
import type { MentoringId } from '#shared/types/Mentoring'
import { useFirebase } from '@/composables/firebase/useFirebase'

/**
 * Composable for managing TOP mentoring 3-month midpoint evaluation.
 * Stored in Firestore: mentorings/{mentoringId}/checkpoints/{id}
 */
export function useTopMidpointEvaluation() {
    const { $firebase } = useFirebase()

    const getCollection = (mentoringId: MentoringId) => {
        return collection($firebase.db, 'mentorings', mentoringId, 'checkpoints')
    }

    /**
     * Load the midpoint evaluation (there is at most one per mentoring)
     */
    const loadEvaluation = async (mentoringId: MentoringId): Promise<TopMidpointEvaluation | null> => {
        const snapshot = await getDocs(getCollection(mentoringId))
        if (snapshot.empty) return null
        const d = snapshot.docs[0]
        return { id: d.id, ...d.data() } as TopMidpointEvaluation
    }

    /**
     * Create the midpoint evaluation document when TOP mentoring starts.
     * Due date = startedAt + 90 days.
     */
    const createEvaluation = async (
        mentoringId: MentoringId,
        startDate: Date = new Date()
    ): Promise<TopMidpointEvaluation> => {
        const dueAt = new Date(startDate)
        dueAt.setDate(dueAt.getDate() + 90)

        const data: Omit<TopMidpointEvaluation, 'id'> = {
            mentoringId,
            dueAt: Timestamp.fromDate(dueAt),
            menteeCompleted: false,
            mentorCompleted: false,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        }

        const docRef = await addDoc(getCollection(mentoringId), data)
        return { ...data, id: docRef.id } as TopMidpointEvaluation
    }

    /**
     * Submit one party's evaluation response.
     * If both parties have now completed, marks the whole evaluation as completed.
     */
    const submitEvaluation = async (
        mentoringId: MentoringId,
        evaluationId: string,
        response: {
            expectationsFulfilled: number // 0-100
            wantToContinue?: 'yes' | 'change'
            comment?: string
        },
        isMentor: boolean
    ): Promise<void> => {
        const ref = doc($firebase.db, 'mentorings', mentoringId, 'checkpoints', evaluationId)
        const prefix = isMentor ? 'mentor' : 'mentee'

        const updateData: Record<string, any> = {
            [`${prefix}ExpectationsFulfilled`]: response.expectationsFulfilled,
            [`${prefix}Comment`]: response.comment || null,
            [`${prefix}Completed`]: true,
            updatedAt: Timestamp.now()
        }

        if (response.wantToContinue) {
            updateData[`${prefix}Continue`] = response.wantToContinue
        }

        await updateDoc(ref, updateData)
    }

    /**
     * Mark evaluation as fully completed (both parties done)
     */
    const completeEvaluation = async (
        mentoringId: MentoringId,
        evaluationId: string
    ): Promise<void> => {
        const ref = doc($firebase.db, 'mentorings', mentoringId, 'checkpoints', evaluationId)
        await updateDoc(ref, {
            completedAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        })
    }

    /**
     * Append one progress reading. Checkpoints are append-only — every save is a new document,
     * so the percentage keeps a history instead of overwriting itself.
     */
    const appendCheckpoint = async (
        mentoringId: MentoringId,
        reading: {
            expectationsFulfilled: number
            comment?: string
            meetingId?: string
        },
        isMentor: boolean
    ): Promise<MentoringCheckpoint> => {
        const data: Omit<MentoringCheckpoint, 'id'> = {
            mentoringId,
            author: isMentor ? 'mentor' : 'mentee',
            expectationsFulfilled: reading.expectationsFulfilled,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        }
        if (reading.comment) data.comment = reading.comment
        if (reading.meetingId) data.meetingId = reading.meetingId

        const docRef = await addDoc(getCollection(mentoringId), data)
        return { ...data, id: docRef.id } as MentoringCheckpoint
    }

    /**
     * Load the full progress series for both parties, oldest first.
     * Legacy single-document evaluations are folded in as one reading per party so the
     * numbers people already entered stay on the curve.
     */
    const loadProgressSeries = async (
        mentoringId: MentoringId
    ): Promise<{ mentor: MentoringCheckpoint[], mentee: MentoringCheckpoint[] }> => {
        const snapshot = await getDocs(getCollection(mentoringId))
        const mentor: MentoringCheckpoint[] = []
        const mentee: MentoringCheckpoint[] = []

        snapshot.docs.forEach((d) => {
            const raw = d.data() as Record<string, any>

            // New shape — one reading, one author.
            if (raw.author === 'mentor' || raw.author === 'mentee') {
                const point = { id: d.id, ...raw } as MentoringCheckpoint
                ;(point.author === 'mentor' ? mentor : mentee).push(point)
                return
            }

            // Legacy shape — one document holding both parties' latest answer.
            const stamp = raw.updatedAt || raw.createdAt || Timestamp.now()
            if (typeof raw.mentorExpectationsFulfilled === 'number') {
                mentor.push({
                    id: `${d.id}:mentor`,
                    mentoringId,
                    author: 'mentor',
                    expectationsFulfilled: raw.mentorExpectationsFulfilled,
                    comment: raw.mentorComment || undefined,
                    createdAt: stamp,
                    updatedAt: stamp
                } as MentoringCheckpoint)
            }
            if (typeof raw.menteeExpectationsFulfilled === 'number') {
                mentee.push({
                    id: `${d.id}:mentee`,
                    mentoringId,
                    author: 'mentee',
                    expectationsFulfilled: raw.menteeExpectationsFulfilled,
                    comment: raw.menteeComment || undefined,
                    createdAt: stamp,
                    updatedAt: stamp
                } as MentoringCheckpoint)
            }
        })

        const byDate = (a: MentoringCheckpoint, b: MentoringCheckpoint) =>
            toMillis(a.createdAt) - toMillis(b.createdAt)

        return { mentor: mentor.sort(byDate), mentee: mentee.sort(byDate) }
    }

    return {
        loadEvaluation,
        createEvaluation,
        submitEvaluation,
        completeEvaluation,
        appendCheckpoint,
        loadProgressSeries
    }
}

/** Firestore Timestamp, Date or ISO string → milliseconds. */
function toMillis(value: any): number {
    if (!value) return 0
    if (typeof value.toMillis === 'function') return value.toMillis()
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? 0 : d.getTime()
}
